import * as React from 'react';
import Table, { TablePaginationConfig } from 'antd/es/table';
import ConfigurationService from '../../Service/ConfigurationService';
import TreeMaps, { clusterMaxZoom } from "./TreeMaps";
import { formatUTCDateFromDateString } from '../../Helper/DateHelper';
import { Tree } from 'tree-wallet-client';
import { ConnectionState, GlobalContext } from "../../Contexts/GlobalContext";
import { PaginationProps } from "antd/lib/pagination/Pagination";
import { Col, Empty, Pagination, Row } from "antd";
import { FilterValue } from 'antd/lib/table/interface';
import { SorterResult } from 'antd/es/table/interface';
import pickBy from "lodash/pickBy";
import { Filter, MapPager } from "./MapPage";

// Where the map camera lands when a row is clicked: the first zoom past the clustering badges
const ROW_CLICK_ZOOM_LEVEL = clusterMaxZoom + 1;

// Longest a line may be in the tree list (the planter's name, the contract description):
// longer text is split between its words instead of being wrapped by the browser
const LINE_MAX_LENGTH = 20;

// Width of each column, as a share of the list's width. The table keeps antd's `auto` layout, so these
// are what the browser aims for and a cell whose content cannot shrink (the Contract's `nowrap`) wins
const DATE_COLUMN_WIDTH = '38%';
const DESCRIPTION_COLUMN_WIDTH = '33%';
const CONTRACT_COLUMN_WIDTH = '29%';

// Air on the left/right edges of each column of the trees list
const DATE_COLUMN_PADDING_LEFT = 12;
const DATE_COLUMN_PADDING_RIGHT = 5;
const DESCRIPTION_COLUMN_PADDING_LEFT = 7;
const DESCRIPTION_COLUMN_PADDING_RIGHT = 5;
const CONTRACT_COLUMN_PADDING_LEFT = 7;
const CONTRACT_COLUMN_PADDING_RIGHT = 5;

// Air between the "Tree ID" label of a row and the id itself
const TREE_ID_GAP = 8;


/** Header and body cells of a column take the padding asked above, which overrides the 4px of every
 *  cell in tree-map-table.less. `separator` marks the title to draw the hairline on its left (the rule
 *  and the hairline's height are in that same stylesheet) */
const columnPadding = (left: number, right: number, separator = false) => ({
  onHeaderCell: () => ({
    className: separator ? 'treelist-separator' : undefined,
    style: { paddingLeft: left, paddingRight: right },
  }),
  onCell: () => ({ style: { paddingLeft: left, paddingRight: right } }),
});

interface TreeTableProps {
  language: string;
  trees?: Tree[];
  paginationConfig: PaginationProps;
  getTrees: Function;
  updateState: Function;
  onDeleteTree: Function;
  pagerData?: MapPager;
  mapRef: React.Ref<TreeMaps>;
  filter: Filter;
}

interface DataType {
  key: React.Key;
  name: string;
  age: number;
  address: string;
}

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string | undefined | null;
  sortOrder?: string | undefined | null;
  filters?: Record<string, FilterValue>;
}

interface FetchDataParams extends TableParams {
  pageSize?: number;
  currentPage?: number;
}

interface TreeTableState extends FetchDataParams {
  loading: boolean;
  /** Tree whose popup was opened from this list: its row is boxed (see `render`'s `rowClassName`) */
  selectedTreeId?: number;
}

/** Splits `value` into lines of at most `maxLength` characters, breaking between words */
function splitLines(value: string, maxLength: number): string[] {
  const lines: string[] = [];
  let line = '';

  value.split(' ').forEach((word) => {
    let rest = word;

    // A word too long for a line of its own is cut into pieces that fit
    while (rest.length > maxLength) {
      if (line) {
        lines.push(line);
        line = '';
      }
      lines.push(rest.slice(0, maxLength));
      rest = rest.slice(maxLength);
    }

    if (line && `${line} ${rest}`.length > maxLength) {
      lines.push(line);
      line = rest;
    } else {
      line = line ? `${line} ${rest}` : rest;
    }
  });

  if (line) {
    lines.push(line);
  }

  return lines;
}

export default class TreeTable extends React.Component<TreeTableProps, TreeTableState> {
  declare context: React.ContextType<typeof GlobalContext>;
  static contextType = GlobalContext;

  constructor(props: TreeTableProps) {
    super(props);
    this.handleTableChange = this.handleTableChange.bind(this);
    this.handlePaginationChange = this.handlePaginationChange.bind(this);
    this.state = {
      sortField: undefined,
      sortOrder: undefined,
      pageSize: props.paginationConfig.pageSize,
      currentPage: props.paginationConfig.current,
      filters: undefined,
      loading: false,
    };
  }

  setLoading(loading: boolean) {
    this.setState({ loading });
  }

  // Open tree popup when a row is clicked
  openTreePopup(record: Tree) {
    const { mapRef } = this.props;

    this.setState({ selectedTreeId: record.id });

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    mapRef?.current?.changePosition({
      coordinates: [record.lng, record.lat],
      treeId: record.id,
      selected: true,
      openPopup: true,
      // Default map zoom level when a tree row has been clicked
      zoomLevel: ROW_CLICK_ZOOM_LEVEL,
    });
  }

  getColumns() {
    return [
      {
        title: 'Date (UTC)',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: DATE_COLUMN_WIDTH,
        align: 'center' as const,
        // Header and cells alike: the padding is set inline, with settings constants all on top of file is the only place
        // (the padding of every cell in tree-map-table.less is overridden here)
        ...columnPadding(DATE_COLUMN_PADDING_LEFT, DATE_COLUMN_PADDING_RIGHT),
        render: (date: any, record: Tree) => {
          const dateStr = formatUTCDateFromDateString(date);
          const photo = record.photos && record.photos.length > 0 && record.photos[0].imageUrl;
          const base = ConfigurationService.urls().baseApiUrl;
          // Online the thumbnail is a much smaller download; offline the trees list copy holds the full photo only
          const photoUrl = photo && (this.context.onlineStatus === ConnectionState.offline
            ? `${base}${photo}`
            : `${base}${photo.replace(/\.(jpg|jpeg)$/, '_thumb.$1')}`);
          return (
            <div style={{ textAlign: 'center' }}>
              {photoUrl &&
                <>
                  <img
                    src={photoUrl}
                    className='treelist-image-thumb'
                    // A portrait photo can use the idle height of its column (see `.portrait` in `main.less`)
                    onLoad={(event) => {
                      const image = event.currentTarget;
                      image.classList.toggle('portrait', image.naturalHeight > image.naturalWidth);
                    }}
                  />
                  <div style={{ height: '16px' }} />
                </>
              }
              <div>{dateStr.date}</div>
              <div>{dateStr.time}</div>
            </div>
          )
        }
      },
      {
        title: 'Description',
        key: 'description',
        width: DESCRIPTION_COLUMN_WIDTH,
        align: 'left' as const,
        ...columnPadding(DESCRIPTION_COLUMN_PADDING_LEFT, DESCRIPTION_COLUMN_PADDING_RIGHT, true),
        render: (record: Tree) => {
          const { language } = this.props;
          const treeType = record.treeType && record.treeType;
          const divStyle = { paddingBottom: '5px' };
          const translation = treeType.translations[language]
            ? treeType.translations[language]
            : treeType.translations.latin;
          return (
            <div style={{ width: '100%', textAlign: 'left' }}>
              <div style={divStyle}>
                <span style={{ fontWeight: 'bold', marginRight: `${TREE_ID_GAP}px` }}>Tree ID</span>{record.id}
              </div>
              <div style={divStyle}>
                {splitLines(`${record.numberPlanted}x ${translation}`, LINE_MAX_LENGTH).map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
                {splitLines(`(${treeType.translations.latin})`, LINE_MAX_LENGTH).map((line, index) => (
                  <div key={index} style={{ fontStyle: 'italic', fontWeight: 'bold' }}>{line}</div>
                ))}
              </div>
              <div style={divStyle}>
                {record.treeType?.fruitTree ? 'fruit tree' : 'shade tree'}
              </div>
              <div style={divStyle}>
                <span style={{ fontWeight: 'bold' }}>planted by</span>
                <div style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                  {splitLines(`${record.user.firstName} ${record.user.lastName}`, LINE_MAX_LENGTH).map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              </div>
              <div style={divStyle}>
                {splitLines(`Accuracy ${record.accuracy} meter${record.accuracy > 1 ? 's' : ''}`, LINE_MAX_LENGTH).map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
              <div style={divStyle}>
                {splitLines(`Altitude ${record.altitude ? `${Math.round(record.altitude)} meter${Math.abs(Math.round(record.altitude)) > 1 ? 's' : ''}` : '-'}`, LINE_MAX_LENGTH).map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
            </div>
          )
        }
      },
      {
        title: splitLines('Contract', LINE_MAX_LENGTH).map((line, index) => (
          <div key={index}>{line}</div>
        )),
        key: 'contract',
        width: CONTRACT_COLUMN_WIDTH,
        align: 'left' as const,
        ...columnPadding(CONTRACT_COLUMN_PADDING_LEFT, CONTRACT_COLUMN_PADDING_RIGHT, true),
        render: (text: string, record: any) => {
          return (
            <div style={{ width: '100%', whiteSpace: 'nowrap', textAlign: 'left' }}>
              {splitLines(record.contract.description ?? '', LINE_MAX_LENGTH).map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </div>
          );
        }
      },
    ];
  }

  async handlePaginationChange(currentPage: number, pageSize: number) {
    this.setState({ loading: true });

    // Get the current filter from props
    const { filter } = this.props;

    // Update the parent's state with the new pagination values and current filter
    this.props.updateState({
      pager: {
        ...this.props.pagerData,
        currentPage,
        pageSize
      },
      filter // Update the filter in parent state
    });

    // Call the parent's getTrees function with the current filter and new pagination
    try {
      await this.props.getTrees(
        undefined,
        filter,
        {
          currentPage,
          pageSize
        }
      );
    } catch (error) {
      // `getTrees` says what failed: the table only has to stop spinning
      console.error('could not read the page of the trees list', { error });
    } finally {
      this.setState({
        loading: false,
        currentPage,
        pageSize
      });
    }
  }

  async handleTableChange(
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue>,
    sorter: SorterResult<DataType>,
  ) {
    const sortOrderValues = {
      'ascend': 'ASC',
      'descend': 'DESC',
    };

    this.setState({ loading: true });
    
    // Get current filter
    const currentFilter = { ...this.props.filter };

    // Prepare the updated filter with sorting
    const updatedFilter = {
      ...currentFilter,
      sortField: sorter.field ? String(sorter.field) : undefined,
      sortOrder: sorter.order ? sortOrderValues[sorter.order] : undefined
    };

    // Update parent state with the new filter and pagination
    this.props.updateState({
      pager: {
        ...this.props.pagerData,
        currentPage: pagination.current,
        pageSize: pagination.pageSize
      },
      filter: updatedFilter
    });

    // Call the parent's getTrees with the updated filter and pagination
    try {
      await this.props.getTrees(
        undefined,
        updatedFilter,
        {
          currentPage: pagination.current,
          pageSize: pagination.pageSize
        }
      );
    } catch (error) {
      // `getTrees` says what failed: the table only has to stop spinning
      console.error('could not sort the trees list', { error });
    } finally {
      this.setState({
        loading: false,
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        sortField: sorter.field ? String(sorter.field) : undefined,
        sortOrder: sorter.order ? sortOrderValues[sorter.order] : undefined
      });
    }
  }

  compileQueryParams(data?: FetchDataParams) {
    const { pageSize, currentPage, sortField, sortOrder } = this.state;

    return {
      currentPage: data?.currentPage ? data?.currentPage : currentPage,
      pageSize: data?.pageSize ? data.pageSize : pageSize,
      sortField: data?.sortField ? data.sortField : sortField,
      sortOrder: data?.sortOrder ? data.sortOrder : sortOrder,
    };
  }

  async fetchData(params?: FetchDataParams) {
    const { getTrees, filter } = this.props;

    // Include the current filter in the request
    const requestParams = {
      ...pickBy(params, v => v !== undefined),
      ...(filter ? { filter } : {}) // Include the filter from props if it exists
    };

    return await getTrees(undefined, undefined, requestParams);
  }

  async refreshData() {
    this.setState({ loading: true });
    const params = this.compileQueryParams();
    await this.fetchData(params);

    this.setState({
      ...params,
      loading: false,
    });
  }

  render() {
    const { paginationConfig, pagerData, trees } = this.props;
    const { loading } = this.state;

    if (!trees || trees.length === 0) {
      return (
        <div>
          <h3 style={{ textAlign: 'center' }}>No tree found</h3>
          <Empty image={Empty.PRESENTED_IMAGE_DEFAULT} />
        </div>
      )
    }

    console.log(trees);

    return (
      <>
        {/* `treelist-pagination`: the tab-size selector goes under the tab numbers (see the less).
            `-top`: on Desktop it stays pinned above the list, together with the column titles */}
        <Row className='treelist-pagination treelist-pagination-top'>
          <Col flex='auto' style={{ minWidth: 0 }}>
            <Pagination
              {...paginationConfig}
              current={pagerData?.currentPage}
              pageSize={pagerData?.pageSize}
              onChange={this.handlePaginationChange}
            />
          </Col>
        </Row>
        <Row>
          {/* Fills the tree list's width so the table and its grey header reach the map component. `alignSelf` keeps the
              wrapper as tall as the table, and on Desktop the Tabs & Columns Titles stays on top while scrolling (see the less) */}
          <Col className='treelist-scroll-col' style={{ flex: 'auto', alignSelf: 'start' }}>
            <Table
              rowKey={'id'}

              onChange={this.handleTableChange}
              columns={this.getColumns()}
              // force table re-render on state change
              dataSource={trees || []}
              pagination={false}
              loading={loading}
              // A click anywhere on a row opens that tree's popup (see `openTreePopup`)
              onRow={(record: Tree) => ({
                onClick: () => this.openTreePopup(record),
                style: { cursor: 'pointer' },
              })}
              // The row that was clicked keeps the rectangle while its tree is the one that was opened
              rowClassName={(record: Tree) => (record.id === this.state.selectedTreeId ? 'treelist-row-selected' : '')}
            />
          </Col>
        </Row>
        <Row className='treelist-pagination'>
          <Col flex='auto' style={{ minWidth: 0 }}>
            <Pagination
              {...paginationConfig}
              current={pagerData?.currentPage}
              pageSize={pagerData?.pageSize}
              onChange={this.handlePaginationChange}
            />
          </Col>
        </Row>
      </>
    );
  }
}
